/**
 * Execution result → the `data` a catalog widget draws (CT-5183, rev4 CT-5641).
 *
 * Almost nothing, now, and that is the point. The rev4 engine shapes the
 * response for the widget's own type before it leaves the backend, and the
 * `@aimsos/ui` catalog widgets were reshaped to read exactly that structure
 * (CT-5641, the branch this one continues). The two halves already agree, so
 * the honest adapter is the one that hands the data over and keeps out of it.
 *
 * What it replaced: a map from three result grains onto eight renderer
 * families — `VALUE_FAMILY`, `PLOT_FAMILY`, `SLICE_FAMILY` — each guessing a
 * widget's shape from columns and rows the executor had not shaped for it. A
 * heat map, a board or an AI summary had no honest reading at all and fell
 * back to sample data. Those are the shapes the backend now sends.
 *
 * It takes no type any more either. Checking `outputContract.typeCode`
 * against the type being drawn looks like cheap insurance and is not: the
 * same drawing travels under several names — `chart` and `area` in the
 * catalogue, `stat_row` beside `METRIC_ROW` in the registry's own aliases —
 * so the check would reject real data for nine of the twenty-nine types. The
 * backend shapes for the widget's stored type, which is the type being drawn.
 */

import { RESULT_STATUS_READY } from '../../../services/widgetData.js';
import { withOrderedSeries } from './temporalSeries.js';

/**
 * @param {object|null} result A `mapApiWidgetResultToUi` result.
 * @returns {object|null} The shaped data, or `null` when there is none to
 *   draw — which leaves `CatalogWidget` on its sample.
 */
export function toWidgetData(result) {
  if (!result || result.status !== RESULT_STATUS_READY) return null;
  if (!result.data || typeof result.data !== 'object') return null;
  /*
    The one thing it does touch: a series grouped by a date comes back in no
    particular order, and a line drawn that way is unreadable. See
    `temporalSeries.js` — it is a presentation fix on this side of the wire,
    and it should go once the engine sorts its own buckets.
  */
  return withOrderedSeries(result.data);
}

export default toWidgetData;
